import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { REST_API } from 'src/constants/Defaultvalues';

/**
 * WhatsApp-style unread for Design Notes / Job Notes tabs.
 * hasNew is true only when the current user is in the message TO list (not the sender).
 * Each recipient has their own last-seen — one user viewing does not clear others.
 *
 * Badge is hidden until the first check for the current board ids finishes,
 * so navigating in does not flash NEW from a premature requestId-only check.
 */
export default function useJobNoteTabUnread({
  requestId = null,
  designOrderId = null,
  noteType = 'design',
  userId,
  enabled = true,
  isActiveTab = false,
  activeTab = null,
}) {
  const [hasNew, setHasNew] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const mountedRef = useRef(true);
  const isActiveTabRef = useRef(isActiveTab);
  const boardKeyRef = useRef('');

  const fromStatus = noteType === 'job' ? 2 : 1;
  const resolvedRequestId = requestId || null;
  const resolvedDesignOrderId = designOrderId || null;
  const boardKey = `${resolvedRequestId || 0}:${resolvedDesignOrderId || 0}:${fromStatus}`;

  useEffect(() => {
    isActiveTabRef.current = isActiveTab;
  }, [isActiveTab]);

  // When board ids change (e.g. details.design_order_id arrives), hide badge until re-checked
  useEffect(() => {
    if (boardKeyRef.current === boardKey) return;
    boardKeyRef.current = boardKey;
    setHasNew(false);
    setHasChecked(false);
  }, [boardKey]);

  const fetchUnread = useCallback(async () => {
    if (!userId || (!resolvedRequestId && !resolvedDesignOrderId)) return;
    if (isActiveTabRef.current) {
      setHasNew(false);
      setHasChecked(true);
      return;
    }
    const requestBoardKey = boardKey;
    try {
      const res = await axios.post(`${REST_API}webservice/request/check-job-note-unread`, {
        userId,
        requestId: resolvedRequestId,
        designOrderId: resolvedDesignOrderId,
        from_status: fromStatus,
      });
      // Ignore stale responses if board ids changed while request was in flight
      if (!mountedRef.current || boardKeyRef.current !== requestBoardKey) return;
      if (res.data?.status === 1) {
        setHasNew(!!res.data.hasUnread);
        setHasChecked(true);
      }
    } catch (error) {
      console.error('Error checking job note unread:', error);
      if (mountedRef.current && boardKeyRef.current === requestBoardKey) {
        setHasChecked(true);
      }
    }
  }, [userId, resolvedRequestId, resolvedDesignOrderId, fromStatus, boardKey]);

  const clearNew = useCallback(() => {
    setHasNew(false);
    setHasChecked(true);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !userId || (!resolvedRequestId && !resolvedDesignOrderId)) {
      setHasNew(false);
      setHasChecked(false);
      return undefined;
    }

    if (isActiveTab) {
      setHasNew(false);
      setHasChecked(true);
      return undefined;
    }

    fetchUnread();
    return undefined;
  }, [enabled, userId, resolvedRequestId, resolvedDesignOrderId, isActiveTab, activeTab, fetchUnread]);

  return {
    // Never show NEW until the check for *this* board key has finished
    hasNew: hasChecked && hasNew && !isActiveTab,
    clearNew,
    refreshUnread: fetchUnread,
  };
}
